import React, {
  useEffect,
  useRef,
  useState
} from "react";

import {
  NodeViewContent,
  NodeViewWrapper
} from "@tiptap/react";


const DEFAULT_DIMENSIONS = {

  rectangle: {
    width: 360,
    height: 150
  },

  circle: {
    width: 220,
    height: 220
  },

  oval: {
    width: 380,
    height: 190
  },

  arrow: {
    width: 400,
    height: 160
  }

};


const clamp = (
  value,
  minimum,
  maximum
) => {

  return Math.min(
    maximum,
    Math.max(
      minimum,
      value
    )
  );

};


const normalizeRotation = (
  value
) => {

  let rotation =
    value % 360;

  if (rotation > 180) {
    rotation -= 360;
  }

  if (rotation < -180) {
    rotation += 360;
  }

  return Math.round(rotation);

};


const hexToRgba = (
  color,
  opacity
) => {

  const validColor =
    /^#[0-9a-f]{6}$/i.test(
      color || ""
    )
      ? color
      : "#ffffff";

  const validOpacity =
    clamp(
      Number(opacity ?? 100),
      0,
      100
    );

  const red =
    parseInt(
      validColor.slice(1, 3),
      16
    );

  const green =
    parseInt(
      validColor.slice(3, 5),
      16
    );

  const blue =
    parseInt(
      validColor.slice(5, 7),
      16
    );

  return `rgba(${red}, ${green}, ${blue}, ${
    validOpacity / 100
  })`;

};


const createGeometry = (
  attributes
) => {

  const shapeType =
    attributes.shapeType ||
    "rectangle";

  const defaults =
    DEFAULT_DIMENSIONS[shapeType] ||
    DEFAULT_DIMENSIONS.rectangle;

  const storedWidth =
    Number(attributes.width);

  const storedHeight =
    Number(attributes.height);

  return {

    width:
      Number.isFinite(storedWidth) &&
      storedWidth >= 8
        ? storedWidth
        : defaults.width,

    height:
      Number.isFinite(storedHeight) &&
      storedHeight >= 8
        ? storedHeight
        : defaults.height,

    offsetX:
      Number(
        attributes.offsetX ?? 0
      ),

    offsetY:
      Number(
        attributes.offsetY ?? 0
      ),

    rotation:
      normalizeRotation(
        Number(
          attributes.rotation ?? 0
        )
      )

  };

};


function RichTextShapeView({
  node,
  updateAttributes,
  selected
}) {

  const transformBoxRef =
    useRef(null);

  const interactionCleanupRef =
    useRef(null);

  const initialGeometry =
    createGeometry(
      node.attrs
    );

  const [
    geometry,
    setGeometry
  ] = useState(initialGeometry);

    const geometryRef =
    useRef(initialGeometry);

  const parsedAnchorImageIndex =
    node.attrs.anchorImageIndex !== null &&
    Number.isInteger(
      Number(
        node.attrs.anchorImageIndex
      )
    )
      ? Number(
          node.attrs.anchorImageIndex
        )
      : null;

  const parsedAnchorCenterYRatio =
    node.attrs.anchorCenterYRatio !== null &&
    Number.isFinite(
      Number(
        node.attrs.anchorCenterYRatio
      )
    )
      ? Number(
          node.attrs.anchorCenterYRatio
        )
      : null;

  const isImageAnchored =
    parsedAnchorImageIndex !== null &&
    parsedAnchorCenterYRatio !== null;


  const applyGeometry = (
    nextGeometry
  ) => {

    geometryRef.current =
      nextGeometry;

    setGeometry(
      nextGeometry
    );

  };


  useEffect(() => {

    const nextGeometry =
      createGeometry(
        node.attrs
      );

    geometryRef.current =
      nextGeometry;

    setGeometry(
      nextGeometry
    );

  }, [
    node.attrs.width,
    node.attrs.height,
    node.attrs.offsetX,
    node.attrs.offsetY,
    node.attrs.rotation,
    node.attrs.shapeType
  ]);


  useEffect(() => {

    return () => {

      if (
        interactionCleanupRef.current
      ) {

        interactionCleanupRef
          .current();

      }

    };

  }, []);

    useEffect(() => {

    const transformBox =
      transformBoxRef.current;

    if (!transformBox) {
      return undefined;
    }

    const editorElement =
      transformBox.closest(
        ".ProseMirror"
      );

    if (
      !isImageAnchored ||
      !editorElement
    ) {

      transformBox.style
        .removeProperty(
          "--shape-editor-anchor-correction-y"
        );

      return undefined;

    }

    const images = [
      ...editorElement.querySelectorAll(
        "img.editor-uploaded-image"
      )
    ];

    const anchorImage =
      images[parsedAnchorImageIndex];

    if (!anchorImage) {

      transformBox.style
        .removeProperty(
          "--shape-editor-anchor-correction-y"
        );

      return undefined;

    }

    let animationFrameId =
      null;

    let layoutSettleTimeoutId =
      null;

    const positionAnchoredShape =
      () => {

        if (
          animationFrameId !== null
        ) {
          window.cancelAnimationFrame(
            animationFrameId
          );
        }

        animationFrameId =
          window.requestAnimationFrame(
            () => {

              animationFrameId =
                null;

              transformBox.style
                .removeProperty(
                  "--shape-editor-anchor-correction-y"
                );

              editorElement
                .getBoundingClientRect();

              const imageRectangle =
                anchorImage
                  .getBoundingClientRect();

              const shapeRectangle =
                transformBox
                  .getBoundingClientRect();

              if (
                imageRectangle.height <= 0
              ) {
                return;
              }

              const desiredCentreY =
                imageRectangle.top +
                parsedAnchorCenterYRatio *
                  imageRectangle.height;

              const currentCentreY =
                shapeRectangle.top +
                shapeRectangle.height / 2;

              const correctionY =
                desiredCentreY -
                currentCentreY;

              transformBox.style
                .setProperty(
                  "--shape-editor-anchor-correction-y",
                  `${correctionY}px`
                );

            }
          );

      };

    const positionAfterLayoutSettles =
      () => {

        positionAnchoredShape();

        if (
          layoutSettleTimeoutId !== null
        ) {
          window.clearTimeout(
            layoutSettleTimeoutId
          );
        }

        layoutSettleTimeoutId =
          window.setTimeout(
            () => {

              layoutSettleTimeoutId =
                null;

              positionAnchoredShape();

            },
            250
          );

      };

    anchorImage.addEventListener(
      "load",
      positionAfterLayoutSettles
    );

    window.addEventListener(
      "resize",
      positionAfterLayoutSettles
    );

    const resizeObserver =
      window.ResizeObserver
        ? new window.ResizeObserver(
            positionAfterLayoutSettles
          )
        : null;

    if (resizeObserver) {

      resizeObserver.observe(
        editorElement
      );

      resizeObserver.observe(
        anchorImage
      );

    }

    positionAfterLayoutSettles();

    return () => {

      if (
        animationFrameId !== null
      ) {
        window.cancelAnimationFrame(
          animationFrameId
        );
      }

      if (
        layoutSettleTimeoutId !== null
      ) {
        window.clearTimeout(
          layoutSettleTimeoutId
        );
      }

      anchorImage.removeEventListener(
        "load",
        positionAfterLayoutSettles
      );

      window.removeEventListener(
        "resize",
        positionAfterLayoutSettles
      );

      resizeObserver?.disconnect();

    };

  }, [
    isImageAnchored,
    parsedAnchorImageIndex,
    parsedAnchorCenterYRatio
  ]);

  const attachPointerTracking = (
    onPointerMove,
    onPointerEnd
  ) => {

    if (
      interactionCleanupRef.current
    ) {

      interactionCleanupRef
        .current();

    }

    const cleanup = () => {

      window.removeEventListener(
        "pointermove",
        onPointerMove
      );

      window.removeEventListener(
        "pointerup",
        finish
      );

      window.removeEventListener(
        "pointercancel",
        finish
      );

      interactionCleanupRef.current =
        null;

    };

    const finish = (
      event
    ) => {

      cleanup();

      onPointerEnd(
        event
      );

    };

    interactionCleanupRef.current =
      cleanup;

    window.addEventListener(
      "pointermove",
      onPointerMove
    );

    window.addEventListener(
      "pointerup",
      finish
    );

    window.addEventListener(
      "pointercancel",
      finish
    );

  };


  const getImageAnchorAttributes =
    () => {

      const transformBox =
        transformBoxRef.current;

      const editorElement =
        transformBox?.closest(
          ".ProseMirror"
        );

      if (
        !transformBox ||
        !editorElement
      ) {
        return {};
      }

      const images = [
        ...editorElement
          .querySelectorAll(
            "img.editor-uploaded-image"
          )
      ];

      if (images.length === 0) {
        return {
          anchorImageIndex: null,
          anchorCenterYRatio: null
        };
      }

      const shapeRectangle =
        transformBox
          .getBoundingClientRect();

      let anchorImage =
        null;

      let largestOverlapArea =
        0;

      images.forEach((image) => {

        const imageRectangle =
          image.getBoundingClientRect();

        const overlapWidth =
          Math.max(
            0,
            Math.min(
              shapeRectangle.right,
              imageRectangle.right
            ) -
            Math.max(
              shapeRectangle.left,
              imageRectangle.left
            )
          );

        const overlapHeight =
          Math.max(
            0,
            Math.min(
              shapeRectangle.bottom,
              imageRectangle.bottom
            ) -
            Math.max(
              shapeRectangle.top,
              imageRectangle.top
            )
          );

        const overlapArea =
          overlapWidth *
          overlapHeight;

        if (
          overlapArea >
          largestOverlapArea
        ) {
          largestOverlapArea =
            overlapArea;

          anchorImage =
            image;
        }

      });

      /*
      A shape remains standalone when it
      does not overlap an inserted image.
      */
      if (!anchorImage) {
        return {
          anchorImageIndex: null,
          anchorCenterYRatio: null
        };
      }

      const imageRectangle =
        anchorImage
          .getBoundingClientRect();

      if (
        imageRectangle.height <= 0
      ) {
        return {};
      }

      const shapeCentreY =
        shapeRectangle.top +
        shapeRectangle.height / 2;

      return {
        anchorImageIndex:
          images.indexOf(
            anchorImage
          ),

        anchorCenterYRatio:
          (
            shapeCentreY -
            imageRectangle.top
          ) /
          imageRectangle.height
      };

    };


  const startMove = (
    event
  ) => {

    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const transformBox =
      transformBoxRef.current;

    if (!transformBox) {
      return;
    }

    const editorElement =
      transformBox.closest(
        ".ProseMirror"
      );

    const referenceWidth =
      transformBox.parentElement
        ?.clientWidth ||
      editorElement?.clientWidth ||
      null;

    const initial =
      {
        ...geometryRef.current
      };

    const initialRectangle =
      transformBox
        .getBoundingClientRect();

    const editorRectangle =
      editorElement
        ? editorElement
            .getBoundingClientRect()
        : null;

    const startPointerX =
      event.clientX;

    const startPointerY =
      event.clientY;


    const handleMove = (
      moveEvent
    ) => {

      const rawDeltaX =
        moveEvent.clientX -
        startPointerX;

      const rawDeltaY =
        moveEvent.clientY -
        startPointerY;

      const currentEditorRectangle =
        editorElement
          ? editorElement
              .getBoundingClientRect()
          : null;

      const editorShiftX =
        editorRectangle &&
        currentEditorRectangle
          ? currentEditorRectangle.left -
            editorRectangle.left
          : 0;

      const editorShiftY =
        editorRectangle &&
        currentEditorRectangle
          ? currentEditorRectangle.top -
            editorRectangle.top
          : 0;

      let deltaX =
        rawDeltaX -
        editorShiftX;

      let deltaY =
        rawDeltaY -
        editorShiftY;

      if (editorRectangle) {

        deltaX =
          clamp(
            deltaX,
            editorRectangle.left -
              initialRectangle.left,
            editorRectangle.right -
              initialRectangle.right
          );

        deltaY =
          Math.max(
            deltaY,
            editorRectangle.top -
              initialRectangle.top
          );

      }

      applyGeometry({
        ...initial,

        offsetX:
          clamp(
            initial.offsetX +
              deltaX,
            -2000,
            2000
          ),

        offsetY:
          clamp(
            initial.offsetY +
              deltaY,
            -2000,
            2000
          )
      });

    };


    const finishMove = () => {

      const finalGeometry =
        geometryRef.current;

      const imageAnchorAttributes =
        getImageAnchorAttributes();

      updateAttributes({
        offsetX:
          finalGeometry.offsetX,

        offsetY:
          finalGeometry.offsetY,

        referenceWidth:
          referenceWidth > 0
            ? Math.round(
                referenceWidth
              )
            : node.attrs
                .referenceWidth,

        ...imageAnchorAttributes
      });

    };


    attachPointerTracking(
      handleMove,
      finishMove
    );

  };


  const startResize = (
    event
  ) => {

    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const transformBox =
      transformBoxRef.current;

    if (!transformBox) {
      return;
    }

    const editorElement =
      transformBox.closest(
        ".ProseMirror"
      );

    const referenceWidth =
      transformBox.parentElement
        ?.clientWidth ||
      editorElement?.clientWidth ||
      null;

    const initial =
      {
        ...geometryRef.current
      };

    const startPointerX =
      event.clientX;

    const startPointerY =
      event.clientY;

    const shapeType =
      node.attrs.shapeType ||
      "rectangle";

    const minimumWidth =
      8;

    const minimumHeight =
      8;

    const maximumWidth =
      editorElement
        ? Math.max(
            minimumWidth,
            Math.min(
              1000,
              editorElement.clientWidth -
                30
            )
          )
        : 1000;


    const handleResize = (
      moveEvent
    ) => {

      const deltaX =
        moveEvent.clientX -
        startPointerX;

      const deltaY =
        moveEvent.clientY -
        startPointerY;

      if (shapeType === "circle") {

        const dominantDelta =
          Math.abs(deltaX) >=
          Math.abs(deltaY)
            ? deltaX
            : deltaY;

        const nextSize =
          clamp(
            initial.width +
              dominantDelta,
            minimumWidth,
            maximumWidth
          );

        applyGeometry({
          ...initial,

          width:
            nextSize,

          height:
            nextSize
        });

        return;

      }

      applyGeometry({
        ...initial,

        width:
          clamp(
            initial.width +
              deltaX,
            minimumWidth,
            maximumWidth
          ),

        height:
          clamp(
            initial.height +
              deltaY,
            minimumHeight,
            800
          )
      });

    };


    const finishResize = () => {

      const finalGeometry =
        geometryRef.current;

      const imageAnchorAttributes =
        getImageAnchorAttributes();

      updateAttributes({
        width:
          Math.round(
            finalGeometry.width
          ),

        height:
          Math.round(
            finalGeometry.height
          ),

        referenceWidth:
          referenceWidth > 0
            ? Math.round(
                referenceWidth
              )
            : node.attrs
                .referenceWidth,

        ...imageAnchorAttributes
      });

    };


    attachPointerTracking(
      handleResize,
      finishResize
    );

  };


  const startRotation = (
    event
  ) => {

    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const transformBox =
      transformBoxRef.current;

    if (!transformBox) {
      return;
    }

    const initial =
      {
        ...geometryRef.current
      };

    const rectangle =
      transformBox
        .getBoundingClientRect();

    const centreX =
      rectangle.left +
      rectangle.width / 2;

    const centreY =
      rectangle.top +
      rectangle.height / 2;

    const startPointerAngle =
      Math.atan2(
        event.clientY - centreY,
        event.clientX - centreX
      ) *
      180 /
      Math.PI;


    const handleRotation = (
      moveEvent
    ) => {

      const currentPointerAngle =
        Math.atan2(
          moveEvent.clientY -
            centreY,
          moveEvent.clientX -
            centreX
        ) *
        180 /
        Math.PI;

      const angleDifference =
        currentPointerAngle -
        startPointerAngle;

      applyGeometry({
        ...initial,

        rotation:
          normalizeRotation(
            initial.rotation +
              angleDifference
          )
      });

    };


    const finishRotation = () => {

      const imageAnchorAttributes =
        getImageAnchorAttributes();

      updateAttributes({
        rotation:
          geometryRef.current
            .rotation,

        ...imageAnchorAttributes
      });

    };


    attachPointerTracking(
      handleRotation,
      finishRotation
    );

  };


  const fillColor =
    hexToRgba(
      node.attrs.fillColor,
      node.attrs.fillOpacity
    );

  const strokeColor =
    hexToRgba(
      node.attrs.strokeColor,
      node.attrs.strokeOpacity
    );

  const alignment =
    node.attrs.alignment ||
    "center";

  const shapeType =
    node.attrs.shapeType ||
    "rectangle";

  const reservedHeight =
    geometry.height +
    Math.max(
      0,
      geometry.offsetY
    );


    const arrowVerticalPadding =
    Math.min(
      40,
      geometry.height * 0.25
    );

  const arrowRightPadding =
    Math.min(
      100,
      geometry.width * 0.25
    );

  const arrowLeftPadding =
    Math.min(
      42,
      geometry.width * 0.105
    );

    const normalizedArrowStrokeWidth =
    Math.min(
      5,
      Math.max(
        1,
        Number(
          node.attrs.strokeWidth ?? 2
        )
      )
    );

  const arrowSvg =
    shapeType === "arrow"
      ? [
          '<svg xmlns="http://www.w3.org/2000/svg" ',
          'viewBox="-5 -5 410 170" ',
          'preserveAspectRatio="none">',
          '<polygon ',
          'points="0,40 260,40 260,0 400,80 ',
          '260,160 260,120 0,120" ',
          `fill="${fillColor}" `,
          `stroke="${strokeColor}" `,
          `stroke-width="${normalizedArrowStrokeWidth}" `,
          'vector-effect="non-scaling-stroke" ',
          'stroke-linejoin="round"/>',
          '</svg>'
        ].join("")
      : "";

  const surfaceStyle =
    shapeType === "arrow"
      ? {
          padding:
            `${
              arrowVerticalPadding
            }px ${
              arrowRightPadding
            }px ${
              arrowVerticalPadding
            }px ${
              arrowLeftPadding
            }px`,

          backgroundColor:
            "transparent",

          backgroundImage:
            `url("data:image/svg+xml,${
              encodeURIComponent(
                arrowSvg
              )
            }")`,

          backgroundPosition:
            "center",

          backgroundRepeat:
            "no-repeat",

          backgroundSize:
            "100% 100%",

          clipPath:
            "none"
        }
      : undefined;


  return (

    <NodeViewWrapper
      className={
        [
          "rich-text-shape-wrapper",
          `rich-text-shape-align-${alignment}`,
          selected
            ? "rich-text-shape-selected"
            : ""
        ]
          .filter(Boolean)
          .join(" ")
      }
            data-rich-text-shape="true"
      data-shape-anchor-image-index={
        isImageAnchored
          ? String(
              parsedAnchorImageIndex
            )
          : undefined
      }
      data-shape-anchor-center-y-ratio={
        isImageAnchored
          ? String(
              parsedAnchorCenterYRatio
            )
          : undefined
      }
      style={{
        "--shape-fill-color":
          fillColor,

        "--shape-stroke-color":
          strokeColor,

        "--shape-stroke-width":
          `${
            Number(
              node.attrs.strokeWidth ??
              2
            )
          }px`,

        minHeight:
          `${reservedHeight}px`
      }}
    >

      <div
        ref={transformBoxRef}
        className="rich-text-shape-transform-box"
        style={{
          width:
            `${geometry.width}px`,

          height:
            `${geometry.height}px`,

          transform:
            `translate(${
              geometry.offsetX
            }px, calc(${
              geometry.offsetY
            }px + var(--shape-editor-anchor-correction-y, 0px))) rotate(${
              geometry.rotation
            }deg)`
        }}
      >

        <div
          className={
            `rich-text-shape-surface rich-text-shape-${shapeType}`
          }
          style={surfaceStyle}
        >

          <NodeViewContent
            className="rich-text-shape-text"
          />

        </div>


        <button
          type="button"
          className="shape-transform-handle shape-move-handle"
          title="Drag to Move Shape"
          aria-label="Move Shape"
          contentEditable={false}
          onPointerDown={startMove}
        >
          ✥
        </button>


        <button
          type="button"
          className="shape-transform-handle shape-rotate-handle"
          title="Drag to Rotate Shape"
          aria-label="Rotate Shape"
          contentEditable={false}
          onPointerDown={startRotation}
        >
          ↻
        </button>


        <button
          type="button"
          className="shape-transform-handle shape-resize-handle"
          title="Drag to Resize Shape"
          aria-label="Resize Shape"
          contentEditable={false}
          onPointerDown={startResize}
        >
          ↘
        </button>

      </div>

    </NodeViewWrapper>

  );

}


export default RichTextShapeView;